import { VIRTUALIZER_OVERSCAN } from '@constants/constants';
import { formatCurrency } from '@utils/common';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode, useEffect, useMemo, useRef } from 'react';

const VIRTUALIZATION_THRESHOLD = 30;
const ROW_HEIGHT = 36;
const VISIBLE_ROW_COUNT = 12;
const LOADER_ROW_COUNT = 4;
const MIN_ROWS_TO_SHOW = 4;
const MAX_ROWS_TO_SHOW = 15;

export type Column<T> = {
  key: keyof T;
  label: string;
  render?: (value: T[keyof T], row: T, level?: number) => ReactNode;
  renderHeader?: (column: Column<T>) => ReactNode;
  secondaryColumns?: Column<T>[];
  customCellStyles?: any;
  customHeaderStyles?: any;
};

export type Row<T> = T & {
  id: string;
  children?: Row<T>[];
  getChildren?: (rowId: string) => Row<T>[];
};

export type CohortTableProps<T> = {
  data: Row<T>[];
  columns: Column<T>[];
  meta?: any;
  isLoading?: boolean;
  customCurrency?: string;
  showCurrencyCode?: boolean;
  getCellBackground?: (value: T[keyof T], cellType: string, row?: Row<T>) => string;
  getTextStyle?: (value: T[keyof T], cellType: string, row?: Row<T>) => any;
  virtualizationEnabled?: boolean;
  isExpanded?: boolean;
  scrollToRowIndex?: number | null;
  scrollAlign?: 'start' | 'center' | 'end';
};

const getCellValueAlignment = (type?: string) => {
  switch (type) {
    case 'percentage':
    case 'currency':
      return 'right';
    case 'string':
    case 'date':
      return 'left';
    default:
      return 'center';
  }
};

const formatCellValue = (
  type: string | undefined,
  value: unknown,
  customCurrency = 'USD',
  showCurrencyCode = false,
  columnMeta?: any,
): ReactNode => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  switch (type) {
    case 'string':
    case 'date':
      return String(value);
    case 'percentage':
      return Number(value) === 0 ? '-' : `${(Number(value) * 100).toFixed(2)}%`;
    case 'currency': {
      if (Number(value) === 0) return '-';
      const isAbbreviated = columnMeta?.currencyFormat?.scale === 'MILLIONS';
      return formatCurrency(
        value as number | string,
        isAbbreviated,
        isAbbreviated ? 2 : 2,
        2,
        { currency: customCurrency },
        showCurrencyCode,
      );
    }
    default:
      return String(value);
  }
};

function ScenarioCohortTable<T>({
  data,
  columns,
  meta,
  isLoading = false,
  customCurrency = 'USD',
  showCurrencyCode = false,
  getCellBackground,
  getTextStyle,
  virtualizationEnabled = true,
  isExpanded = false,
  scrollToRowIndex = null,
  scrollAlign = 'start',
}: CohortTableProps<T>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columnMetadata = useMemo(() => {
    if (!meta?.columns?.length) return {} as Record<string, any>;
    return meta.columns.reduce((acc: Record<string, any>, column: any) => {
      acc[column.id] = column;
      return acc;
    }, {});
  }, [meta?.columns]);

  const shouldVirtualize = data.length > VIRTUALIZATION_THRESHOLD;

  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: VIRTUALIZER_OVERSCAN,
  });

  const getCellBackgroundColor = (value: T[keyof T], cellType: string, row?: Row<T>) => {
    if (getCellBackground) {
      return getCellBackground(value, cellType, row);
    }
    return '#FFFFFF';
  };

  const getCellTextStyle = (value: T[keyof T], cellType: string, row?: Row<T>) => {
    if (getTextStyle) {
      return getTextStyle(value, cellType, row);
    }
    return {};
  };
  const renderHeader = () => (
    <TableHead>
      <TableRow>
        {columns.map((column, index) => (
          <TableCell
            key={String(column.key)}
            align={getCellValueAlignment(columnMetadata[String(column.key)]?.type)}
            sx={{
              paddingY: '0.5rem',
              paddingX: '0.5rem',
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: '1rem',
              color: '#6C6C6C',
              backgroundColor: '#F0F0F1',
              borderBottom: '1px solid #E8EAEB',
              borderRight: index < columns.length - 1 ? '1px solid #E8EAEB' : 'none',
              whiteSpace: 'nowrap',
              ...(column.customHeaderStyles ?? {}),
              height: '40px',
            }}
          >
            {column.renderHeader ? column.renderHeader(column) : column.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const renderBodyCell = (row: Row<T>, column: Column<T>, colIndex: number) => {
    const columnKey = String(column.key);
    const columnType = columnMetadata[columnKey]?.type;
    const cellValue = row[column.key as keyof Row<T>];

    return (
      <TableCell
        key={columnKey}
        align={getCellValueAlignment(columnType)}
        sx={{
          paddingY: '0.5rem',
          paddingX: '0.5rem',
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: '1rem',
          color: '#162C36',
          borderBottom: '1px solid #E8EAEB',
          borderRight: colIndex < columns.length - 1 ? '1px solid #E8EAEB' : 'none',
          backgroundColor: getCellBackgroundColor(cellValue as T[keyof T], columnType, row),
          ...getCellTextStyle(cellValue as T[keyof T], columnType, row),
          whiteSpace: 'nowrap',
          ...(column.customCellStyles ?? {}),
        }}
      >
        {column.render
          ? column.render(cellValue as T[keyof T], row as T)
          : formatCellValue(
              columnType,
              cellValue,
              customCurrency,
              showCurrencyCode,
              columnMetadata[columnKey],
            )}
      </TableCell>
    );
  };

  const renderDataRow = (row: Row<T>, rowIndex: number) => (
    <TableRow
      key={row.id}
      sx={{
        height: `${ROW_HEIGHT}px`,
        '&:last-child td': {
          borderBottom: 'none',
        },
      }}
    >
      {columns.map((column, colIndex) => renderBodyCell(row, column, colIndex))}
    </TableRow>
  );

  const renderVirtualizedBody = () => {
    const virtualRows = rowVirtualizer.getVirtualItems();
    const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
    const paddingBottom =
      virtualRows.length > 0
        ? rowVirtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0)
        : 0;

    return (
      <TableBody>
        {paddingTop > 0 && (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{ height: paddingTop, padding: 0, border: 'none' }}
            />
          </TableRow>
        )}
        {virtualRows.map((virtualRow) => {
          const row = data[virtualRow.index];
          if (!row) return null;
          return renderDataRow(row, virtualRow.index);
        })}
        {paddingBottom > 0 && (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{ height: paddingBottom, padding: 0, border: 'none' }}
            />
          </TableRow>
        )}
      </TableBody>
    );
  };

  const renderStaticBody = () => (
    <TableBody>{data.map((row, rowIndex) => renderDataRow(row, rowIndex))}</TableBody>
  );

  const renderLoadingBody = () => (
    <TableBody>
      <TableRow>
        <TableCell colSpan={columns.length || 1} sx={{ padding: '0.5rem', border: 'none' }}>
          {Array.from({ length: LOADER_ROW_COUNT }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height="1.75rem"
              sx={{
                borderRadius: '0.25rem',
                marginBottom: index < LOADER_ROW_COUNT - 1 ? '0.5rem' : 0,
              }}
            />
          ))}
        </TableCell>
      </TableRow>
    </TableBody>
  );

  const renderEmptyBody = () => (
    <TableBody>
      <TableRow>
        <TableCell
          align="center"
          colSpan={columns.length}
          sx={{
            border: 'none',
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            No data available
          </Typography>
        </TableCell>
      </TableRow>
    </TableBody>
  );

  const hasColumns = columns.length > 0;
  const hasRows = data.length > 0;

  useEffect(() => {
    if (!isExpanded) return;
    if (scrollToRowIndex == null) return;
    if (scrollToRowIndex < 0 || scrollToRowIndex >= data.length) return;

    // Wait one frame so container maxHeight / virtualizer measurements settle
    const id = requestAnimationFrame(() => {
      if (shouldVirtualize && virtualizationEnabled) {
        rowVirtualizer.scrollToIndex(scrollToRowIndex, {
          align: scrollAlign ?? 'start',
          behavior: 'smooth',
        });
      } else if (tableContainerRef.current) {
        const top = scrollToRowIndex * ROW_HEIGHT;
        tableContainerRef.current.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded, scrollToRowIndex, data.length, shouldVirtualize]);
  return (
    <Box
      sx={{
        border: '1px solid #E8EAEB',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <TableContainer
        ref={tableContainerRef}
        sx={{
          width: '100%',
          maxHeight: shouldVirtualize
            ? ROW_HEIGHT * VISIBLE_ROW_COUNT + ROW_HEIGHT
            : data.length > MIN_ROWS_TO_SHOW
              ? ROW_HEIGHT * Math.min(data.length, MAX_ROWS_TO_SHOW)
              : 'auto',
          overflowX: 'auto',
          overflowY: shouldVirtualize ? 'auto' : 'visible',
        }}
      >
        <Table
          stickyHeader={shouldVirtualize || data.length > MAX_ROWS_TO_SHOW}
          size="small"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: shouldVirtualize ? 'fixed' : 'auto',
            width: '100%',
            minWidth: shouldVirtualize ? `${columns.length * 150}px` : 'max-content',
          }}
        >
          {hasColumns && (
            <colgroup>
              {columns.map((column) => (
                <col key={String(column.key)} style={{ minWidth: 80 }} />
              ))}
            </colgroup>
          )}
          {hasColumns && renderHeader()}

          {isLoading
            ? renderLoadingBody()
            : !hasColumns || !hasRows
              ? renderEmptyBody()
              : shouldVirtualize && virtualizationEnabled
                ? renderVirtualizedBody()
                : renderStaticBody()}
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ScenarioCohortTable;
